/**
 * Office email/password auth dialog — opened via displayDialogAsync from the taskpane.
 * Posts Famla JWT back to the parent via messageParent after POST /auth/login.
 */
import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Buttons, InputForm, Spiner } from '../../../components';
import TurnstileWidget from '../../../components/auth/TurnstileWidget';
import { Images, Metrics } from '../../../constants';
import { useBranding } from '../../../contexts/BrandingContext';
import { useTheme } from '../../../contexts/ThemeContext';
import FamlaServices from '../../../services/api/FamlaServices';
import { config } from '../../../services/config';
import '../../../features/auth/UsersLogin.css';
import type { OfficeEmailAuthDialogResult } from './openOfficeEmailAuthDialog';
import './OfficeEmailAuthDialogEntry.css';

const OFFICE_JS_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

function postResultToParent(result: OfficeEmailAuthDialogResult): void {
    const officeUi = (Office as any)?.context?.ui;
    if (officeUi && typeof officeUi.messageParent === 'function') {
        officeUi.messageParent(JSON.stringify(result));
        return;
    }

    if (process.env.REACT_APP_DEBUG === 'true') {
        // eslint-disable-next-line no-console
        console.warn('[OfficeEmailAuthDialog] messageParent unavailable', result);
    }
}

async function waitForOfficeJs(): Promise<void> {
    if (typeof Office !== 'undefined') {
        if (typeof (Office as any).onReady === 'function') {
            await (Office as any).onReady();
        }
        return;
    }

    await new Promise<void>((resolve, reject) => {
        const existing = document.getElementById('office-js') as HTMLScriptElement | null;
        const script = existing || document.createElement('script');
        if (!existing) {
            script.id = 'office-js';
            script.src = OFFICE_JS_URL;
            script.async = true;
            document.head.appendChild(script);
        }
        script.onload = () => {
            void (Office as any).onReady?.().then(resolve).catch(reject);
        };
        script.onerror = () => reject(new Error('Office.js failed to load'));
    });
}

function isTurnstileRequiredResponse(data: {
    status?: number;
    body?: { response_type?: string; data?: { turnstile_required?: boolean; error_code?: string } };
}): boolean {
    if (data.status !== 403) return false;
    const body = data.body;
    return (
        body?.response_type === 'captcha_required' ||
        body?.data?.turnstile_required === true ||
        body?.data?.error_code === 'TURNSTILE_REQUIRED' ||
        body?.data?.error_code === 'TURNSTILE_INVALID'
    );
}

const OfficeEmailAuthDialogEntry: React.FC = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { getLogo, isCustomBranding, branding } = useBranding();
    const displayLogo = getLogo(theme) || Images.logov1;
    const appName = isCustomBranding ? branding.appName : 'Famla AI';

    const [officeReady, setOfficeReady] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isValidMail, setIsValidMail] = useState(true);
    const [isSubmit, setIsSubmit] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [turnstileRequired, setTurnstileRequired] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);

    useEffect(() => {
        document.documentElement.classList.add('office-email-auth-dialog');
        document.body.classList.add('office-email-auth-dialog');
        return () => {
            document.documentElement.classList.remove('office-email-auth-dialog');
            document.body.classList.remove('office-email-auth-dialog');
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        void waitForOfficeJs()
            .then(() => {
                if (!cancelled) setOfficeReady(true);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Office.js failed to load');
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const resetTurnstileWidget = useCallback(() => {
        setTurnstileToken(null);
        setTurnstileResetSignal((n) => n + 1);
    }, []);

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const trimmed = value.trim();
        setEmail(value);
        setIsValidMail(trimmed.length === 0 || Metrics.mailFormat.test(trimmed));
        setError(null);
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
        setError(null);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);

        const emailTrim = email.trim();
        if (!emailTrim) {
            setError(t('views.auth.emailIsRequired'));
            return;
        }
        if (!Metrics.mailFormat.test(emailTrim)) {
            setIsValidMail(false);
            setError(t('views.auth.enterValidMail'));
            return;
        }
        if (!password) {
            setError(t('views.auth.passwordIsRequired'));
            return;
        }
        if (turnstileRequired && !turnstileToken) {
            setError(t('views.auth.captchaMissing'));
            return;
        }

        setIsSubmit(true);
        const formData: { email: string; password: string; turnstile_token?: string } = {
            email: emailTrim,
            password,
        };
        if (turnstileRequired && turnstileToken) {
            formData.turnstile_token = turnstileToken;
        }

        try {
            const data = await FamlaServices('api_host', '/auth/login', 'POST', formData, '');

            if (turnstileRequired) {
                resetTurnstileWidget();
            }

            if (data.status === 200 && data.body?.data?.access_token) {
                postResultToParent({
                    status: 'success',
                    accessToken: data.body.data.access_token,
                    refreshToken: data.body.data.refresh_token || undefined,
                });
                return;
            }

            if (data.status === 403 && data.body?.data?.sso_portal_url) {
                setError(t('office.auth.corporateSsoRequired'));
                setIsSubmit(false);
                return;
            }

            if (isTurnstileRequiredResponse(data)) {
                const errorCode = String(data.body?.data?.error_code ?? '');
                setTurnstileRequired(true);
                setError(
                    errorCode === 'TURNSTILE_INVALID'
                        ? t('views.auth.captchaInvalid')
                        : t('views.auth.captchaRequired')
                );
                setIsSubmit(false);
                return;
            }

            if (data.status === 403 && data.body?.response_type === 'email_not_verified') {
                setError(t('office.auth.emailNotVerified'));
                setIsSubmit(false);
                return;
            }

            if (data.status === 400) {
                setError(t('views.auth.incorrectEmailOrPassword'));
                setPassword('');
                setIsSubmit(false);
                return;
            }

            setError(
                data.body?.description ||
                    t('office.auth.emailSignInFailed', 'Sign-in failed. Please try again.')
            );
            setIsSubmit(false);
        } catch (err) {
            if (turnstileRequired) {
                resetTurnstileWidget();
            }
            setError(
                err instanceof Error
                    ? err.message
                    : t('office.auth.emailSignInFailed', 'Sign-in failed. Please try again.')
            );
            setIsSubmit(false);
        }
    };

    if (!officeReady && !error) {
        return (
            <div className="office-email-auth-dialog__shell">
                <div className="office-email-auth-dialog__loading">
                    <Spiner className="d-flex justify-content-center spinner-border-sm" />
                    <p>{t('office.sidebar.loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="office-email-auth-dialog__shell">
            <div className="users-login-light office-email-auth-dialog__panel">
                <img
                    className="office-email-auth-dialog__logo"
                    src={displayLogo}
                    alt={appName}
                />

                <div className="users-login-title-block">
                    <h1 className="users-login-headline office-email-auth-dialog__headline">
                        {t('views.auth.signInToYourAccount')}
                    </h1>
                    <p className="office-email-auth-dialog__subtitle">
                        {t('office.auth.emailDialogSubtitle', 'Use your Famla email and password.')}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    <InputForm
                        inputType="email"
                        inputLabel={t('views.auth.emailAddresPro')}
                        inputName="email"
                        inputValue={email}
                        isFloatingLabel={true}
                        labelClass="text-theme"
                        onChange={handleEmailChange}
                        inputContainerClass={isValidMail ? 'mb-3' : 'mb-0'}
                    />
                    {!isValidMail && (
                        <span className="text-danger fs-6 d-bloc mb-3">
                            {t('views.auth.enterValidMail')}
                        </span>
                    )}

                    <InputForm
                        inputType="password"
                        inputLabel={t('views.auth.password')}
                        inputName="password"
                        inputValue={password}
                        isFloatingLabel={true}
                        labelClass="text-theme"
                        onChange={handlePasswordChange}
                        inputContainerClass="mb-3"
                        withIcon={true}
                        iconClass="text-theme"
                    />

                    {turnstileRequired && config.auth.turnstileSiteKey && (
                        <TurnstileWidget
                            siteKey={config.auth.turnstileSiteKey}
                            onToken={setTurnstileToken}
                            resetSignal={turnstileResetSignal}
                            className="users-login-turnstile"
                        />
                    )}

                    <Buttons
                        buttonType="submit"
                        name={t('views.auth.logIn')}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            void handleSubmit(event);
                        }}
                        isLoading={isSubmit}
                        btnClass="users-login-submit w-100"
                        labelClass="text-white fw-bold"
                        styleProps={{ minHeight: 48 }}
                        arrowVisible={false}
                        spinnerColor="text-white"
                        arrowColor=""
                        disabled={
                            password.length === 0 ||
                            email.trim().length === 0 ||
                            !isValidMail ||
                            (turnstileRequired && !turnstileToken)
                        }
                    />
                </form>
            </div>
        </div>
    );
};

export default OfficeEmailAuthDialogEntry;
